import { useTranslation } from 'react-i18next';
import { Ban, Check, X } from 'lucide-react';
import { AdvanceBatchDetail } from '@/types';
import { formatDateByDayjs } from '@/utils';
import { localizeDigits, merchantFailureReason } from './format';
import { getLifecycleNodes, LifecycleNode, LIFECYCLE_STEPS } from './lifecycle';

interface BatchLifecycleStepperProps {
  batch: AdvanceBatchDetail;
  /** scrolls to the excluded-orders notice when the partial badge is tapped */
  onPartialBadgeClick?: () => void;
}

/**
 * Horizontal 5-step batch lifecycle (DESIGN v2.1): Submitted → Approved →
 * Disbursed → In Progress → Closed, with completed/current/pending/rejected/
 * not-applicable node states. Reuses TrackingTimeline's color tokens
 * (green check / grey connector); the current node pulses via
 * `motion-safe:animate-pulse-ring`.
 */
function BatchLifecycleStepper({ batch, onPartialBadgeClick }: BatchLifecycleStepperProps) {
  const { t } = useTranslation();
  const nodes = getLifecycleNodes(batch.status);

  const stepTimestamp = (key: (typeof LIFECYCLE_STEPS)[number]): string | null => {
    if (key === 'submitted') return batch.submitted_at ?? batch.created_at;
    if (key === 'approved') return batch.decided_at;
    if (key === 'disbursed') return batch.disbursed_at;
    if (key === 'closed') return batch.closed_at;
    return null; // in_progress has no dedicated timestamp column
  };

  const circleClass = (node: LifecycleNode): string => {
    if (node.state === 'completed') return 'bg-[rgb(49,196,127)]';
    if (node.state === 'current')
      return 'bg-[rgb(237,224,74)] motion-safe:animate-pulse-ring';
    if (node.state === 'rejected') return 'bg-[rgb(220,40,40)]';
    if (node.state === 'not_applicable')
      return 'border border-dashed border-[rgb(220,40,40)]/30 bg-transparent';
    return 'bg-white border-2 border-solid border-[rgb(232,230,227)]';
  };

  const labelClass = (node: LifecycleNode): string => {
    if (node.state === 'completed' || node.state === 'current')
      return 'text-[12px] font-bold text-[rgb(57,57,60)]';
    if (node.state === 'rejected') return 'text-[12px] font-bold text-[rgb(220,40,40)]';
    if (node.state === 'not_applicable')
      return 'text-[12px] font-medium text-[rgb(160,160,158)] line-through decoration-[rgb(220,40,40)]/40';
    return 'text-[12px] font-medium text-[rgb(160,160,158)]';
  };

  // Connector segment feeding INTO node i (i>0): green when the previous
  // node completed, grey otherwise.
  const connectorClass = (index: number): string => {
    const prev = nodes[index - 1];
    return prev && prev.state === 'completed' ? 'bg-[rgb(49,196,127)]' : 'bg-[rgb(232,230,227)]';
  };

  return (
    <section
      className='rounded-2xl border border-solid border-[rgb(232,230,227)] bg-white p-5'
      data-testid='batch-lifecycle-stepper'
    >
      <div className='flex items-start justify-between overflow-x-auto'>
        {nodes.map((node, index) => {
          const timestamp = stepTimestamp(node.key);
          // failure_reason is a technical `CODE: message` string — map it to
          // merchant-readable copy (QA defect 16). decision_reason comes
          // from FFC as display text and passes through.
          const rejectionReason =
            node.key === 'submitted'
              ? merchantFailureReason(t as (key: string) => string, batch.failure_reason)
              : batch.decision_reason;
          return (
            <div key={node.key} className='contents'>
              {index > 0 ? (
                <div className={`flex-1 h-0.5 self-center mt-[15px] min-w-[16px] ${connectorClass(index)}`} />
              ) : null}
              <div className='flex flex-col items-center gap-1.5 min-w-[88px]'>
                <div className='relative'>
                  <div
                    className={`w-[30px] h-[30px] rounded-full flex items-center justify-center ${circleClass(node)}`}
                    data-testid={`lifecycle-node-${node.key}`}
                    data-state={node.state}
                  >
                    {node.state === 'completed' ? (
                      <Check className='w-4 h-4 text-white' strokeWidth={3} />
                    ) : node.state === 'rejected' ? (
                      <X className='w-4 h-4 text-white' strokeWidth={3} />
                    ) : node.state === 'not_applicable' ? (
                      <Ban className='w-4 h-4 text-[rgb(220,40,40)]/40' />
                    ) : (
                      // digit style follows the UI language (QA defect 11)
                      <span className='text-[12px] font-bold text-[rgb(57,57,60)]'>
                        {localizeDigits(index + 1)}
                      </span>
                    )}
                  </div>
                  {node.partial ? (
                    <button
                      type='button'
                      onClick={onPartialBadgeClick}
                      aria-label={String(t('advance_cod_view_excluded_orders'))}
                      data-testid='lifecycle-partial-badge'
                      className='absolute -end-0.5 -top-0.5 w-3.5 h-3.5 rounded-full bg-[rgb(187,120,27)] border-2 border-solid border-white cursor-pointer p-0'
                    />
                  ) : null}
                </div>
                <span className={labelClass(node)}>
                  {String(t(`advance_lifecycle_step.${node.key}`))}
                </span>
                {node.state === 'current' ? (
                  <span className='text-[10px] text-[rgb(187,120,27)]'>
                    {String(t('lifecycle_step_in_progress'))}
                  </span>
                ) : node.state === 'rejected' && rejectionReason ? (
                  <span className='text-[10px] text-[rgb(220,40,40)] mt-0.5 italic text-center'>
                    {rejectionReason}
                  </span>
                ) : timestamp && node.state === 'completed' ? (
                  <span className='text-[10px] text-[rgb(115,123,140)] mt-0.5'>
                    <bdi>{formatDateByDayjs(timestamp, 'D MMM YYYY, HH:mm')}</bdi>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default BatchLifecycleStepper;
